// hooks
import { useFormik, setNestedObjectValues } from 'formik';
import { useEffect } from 'react';
import { useDraftCaseApi } from '../../../../../../../apiHooks';
import { useParams } from 'react-router-dom';
import { useProgressBarStore } from '../../../../../../../store/hooks/useProgressBarStore';

// types, helpers
import { IFirstStageValuesCushingV2 } from './FirstStage.types';
import { initialFirstStage, validationFirstStageSchema } from './FirstStage.helper';
import { DiseaseType } from '../../../../../../../interfaces/common/common';
import { Action } from '../../../../../../../store/progressBarStore';

export const useFirstStageVm = () => {
  const { id, type, version } = useParams();
  const { draftCaseFirstStage, draftCaseFirstStageSaved } = useDraftCaseApi();
  const progressBarStore = useProgressBarStore();

  const formik = useFormik<IFirstStageValuesCushingV2>({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: false,
    initialValues: (progressBarStore.firstStage as IFirstStageValuesCushingV2) ?? initialFirstStage,
    validationSchema: validationFirstStageSchema,
    onSubmit: async values => {
      await draftCaseFirstStage(formik.values, Number(id), type as DiseaseType, version);
      progressBarStore.setFirstStage(values);
    }
  });

  const { setFieldValue } = formik;

  useEffect(() => {
    if (!formik.values.complicationsOfHypercortisolism.includes('powikłania zakrzepowo-zatorowe')) {
      setFieldValue('thromboembolicComplications', []);
    }
    if (!formik.values.complicationsOfHypercortisolism.includes('złamania')) {
      setFieldValue('descriptionOfFractures', '');
    }
  }, [formik.values.complicationsOfHypercortisolism, setFieldValue]);


  useEffect(() => {
    if (!formik.values.symptoms.includes('zaburzenia psychiatryczne')) {
      setFieldValue('psychiatricDisorders', []);
    }
  }, [formik.values.symptoms, setFieldValue]);

  useEffect(() => {
    if (Number(formik.values.weight) > 0 && Number(formik.values.height) > 0) {
      const countBMI = Math.round(Number(formik.values.weight) / Math.pow(formik.values.height / 100, 2));
      const bmiValue = Number.isNaN(countBMI) ? '' : countBMI;
      const bmi = Number.isNaN(bmiValue) && Number(bmiValue) < 0 ? '' : bmiValue;
      setFieldValue('bmi', bmi);
    } else {
      setFieldValue('bmi', -1);
    }
  }, [formik.values.height, formik.values.weight, setFieldValue]);

  const handleChangeArray = (name: keyof IFirstStageValuesCushingV2, value: string) => {
    let newValues = [...(formik.values[name] as string[])];
    if (newValues.find(element => element === value)) {
      newValues = newValues.filter(element => element !== value);
    } else {
      newValues.push(value);
    }

    formik.setFieldValue(name, newValues);
  };

  const handleSave = async () => {
    const errors = await formik.validateForm();
    formik.setTouched(setNestedObjectValues(formik.values, true));

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    }
  };

  const handleDraftSave = async (action: Action) => {
    await draftCaseFirstStageSaved(formik.values, Number(id), type as DiseaseType, version);
    progressBarStore.setFirstStage(formik.values, action);
  };

  const setOption = (value: number | string | string[], field: string) => {
    setFieldValue(field, value);
  };

  const addComorbidity = () => {
    const { comorbidities } = formik.values;
    setFieldValue('comorbidities', [...comorbidities, '']);
  };

  const handleDeleteComorbidity = (index: number) => {
    const { comorbidities } = formik.values;
    const newComorbidities = [...comorbidities];
    newComorbidities.splice(index, 1);
    setFieldValue('comorbidities', newComorbidities);
  };

  return {
    type: type as DiseaseType,
    handleChangeArray,
    formik,
    handleSave,
    handleDraftSave,
    setOption,
    addComorbidity,
    handleDeleteComorbidity
  };
};
